import { useCallback, useMemo, useState } from "react";

/**
 * Row selection state for a server-paged antd table.
 *
 * The table only ever holds the current page, so the selected rows themselves are remembered in
 * a map keyed by id. That keeps a selection made across several pages intact and lets the caller
 * read real fields off the selected rows - how many carry a PDF, for instance - without
 * refetching them.
 */
export default function useTableSelection() {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [rowsById, setRowsById] = useState({});

    const handleChange = useCallback((keys, rows) => {
        setSelectedRowKeys(keys);

        setRowsById((prev) => {
            const next = {};

            // Carry over rows selected on other pages, which antd cannot hand back here.
            keys.forEach((key) => {
                if (prev[key]) next[key] = prev[key];
            });

            // Rows on the current page always win, so a reloaded row is the fresh one.
            (rows || []).forEach((row) => {
                if (row && row.id !== undefined && row.id !== null) next[row.id] = row;
            });

            return next;
        });
    }, []);

    const clear = useCallback(() => {
        setSelectedRowKeys([]);
        setRowsById({});
    }, []);

    const selectedRows = useMemo(
        () => selectedRowKeys.map((key) => rowsById[key]).filter(Boolean),
        [selectedRowKeys, rowsById]
    );

    const rowSelection = useMemo(
        () => ({
            selectedRowKeys,
            onChange: handleChange,
            // Selecting on page 2 must not drop what was ticked on page 1.
            preserveSelectedRowKeys: true,
            columnWidth: 48,
            fixed: true,
        }),
        [selectedRowKeys, handleChange]
    );

    return {
        selectedRowKeys,
        selectedRows,
        selectedCount: selectedRowKeys.length,
        rowSelection,
        clear,
    };
}
